import * as z from 'zod';

import { AddressSchema } from '#packages/data-collection/models/address';
import { AttachmentSchema } from '#packages/data-collection/models/common/attachment';

import { isoDateTimeToMoment } from '../../codecs/moment';

export const MeterSchema = z.object({
  type: z.enum(['electricity', 'natural_gas', 'water', 'other']).nonoptional(),
  location: z.string().optional(),
  serialNumber: z.string().optional(),
  description: z.string().optional(),
  attachments: z.array(AttachmentSchema).optional(),
});

export type Meter = z.infer<typeof MeterSchema>;

export const SiteContactSchema = z.object({
  name: z.string().nonempty(),
  role: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
});

export type SiteContact = z.infer<typeof SiteContactSchema>;

export const AuditorTeamSchema = z.object({
  name: z.string().nonempty(),
  tasks: z.string().optional(),
});

export type AuditorTeam = z.infer<typeof AuditorTeamSchema>;

export const MurbSuiteSchema = z.object({
  type: z.coerce.number().nonoptional(), // predefined
  num: z.coerce.number().nonoptional(),
});

export type MurbSuite = z.infer<typeof MurbSuiteSchema>;

export const OperationScheduleEntrySchema = z.object({
  weekDay: z.number().int().min(0).max(6),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  isUnavailable: z.boolean().optional(),
});

export type OperationScheduleEntry = z.infer<
  typeof OperationScheduleEntrySchema
>;

export const GeneralSchema = z.object({
  organizationName: z.string().optional(),
  buildingName: z.string().optional(),
  address: AddressSchema,
  siteContacts: z.array(SiteContactSchema).optional(),
  auditorTeam: z.array(AuditorTeamSchema).optional(),
  siteVisitStartDate: isoDateTimeToMoment.optional(),
  siteVisitEndDate: isoDateTimeToMoment.optional(),

  weatherAtStart: z.string().optional(),
  weatherAtEnd: z.string().optional(),

  // Uninsulated piping in mech room (ft):
  heatingHotWater: z.coerce.number().optional(), // HHW
  domesticHotWater: z.coerce.number().optional(), // DHW

  // Construction year fields:
  yearBuilt: z.coerce.number().optional(), // 4-digit number
  isYearBuiltApproximate: z.boolean().default(false),
  renovations: z.string().optional(),

  grossFloorArea: z.coerce.number().optional(), // gross floor area (m2)
  floors: z.coerce.number().optional(), // above ground stories
  parkadeLevels: z.coerce.number().optional(),
  otherFloors: z.coerce.number().optional(),

  operationSchedule: z.array(OperationScheduleEntrySchema).optional(),
  operationHoursDescription: z.string().optional(),

  murbSuites: z.array(MurbSuiteSchema).optional(),

  occupants: z.object({
    fullTimeEmployees: z.coerce.number().optional(),
    visitors: z.coerce.number().optional(),
    retailCustomers: z.coerce.number().optional(),
    k12Students: z.coerce.number().optional(),
    residentialTenants: z.coerce.number().optional(),
    description: z.string().optional(),
  }),

  occupantsDescription: z.string().optional(),
  spaceUsesDescription: z.string().optional(),

  meters: z.array(MeterSchema).optional(),

  elevators: z.coerce.number().optional(),
  evChargers: z
    .object({
      count: z.coerce.number().optional(),
      location: z.string().optional(),
      isFedByHouseMeter: z.boolean().optional(),
    })
    .optional(),
  exteriorCarOutlets: z.coerce.number().optional(),

  recentUpgrades: z.string().optional(),
  noGoProjects: z.string().optional(),
  plansForUpgrades: z.string().optional(),
  energyOpportunities: z.string().optional(),
});

export type General = z.infer<typeof GeneralSchema>;
